import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Grid, Skeleton, Typography } from '@mui/material';
import { GridRowParams, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { useCurrency } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { DemandRequestWidgetConfig } from '../../DemandRequestWidget.config';
import { useDemandRequestGridConfig } from '../../DemandRequestWidget.dataGrid';
import { TsrctDemandRequest } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { CuriosityLinkChip } from '~/components/CuriosityLinkChip/CuriosityLinkChip';
import { DataGridProStyled, DataGridWidgetHeader, NULLISH_CELL_VALUE } from '~/components/DataGridCustom';
import { RowDetailPanel } from '~/components/DataGridCustom/components/RowDetailPanel/RowDetailPanel';
import { useModuleConfig } from '~/components/ModuleConfigProvider';
import { Multiline } from '~/components/Multiline';
import { WidgetField } from '~/components/WidgetField/WidgetField';
import { PurchaseRequisitionSourceReference } from '~/modules/DemandDetailWidget/components/PurchaseRequisitionSourceReference/PurchaseRequisitionSourceReference';

export interface DemandRequestDetailsProps {
  data: {
    id: string;
    request: Union.Nullable<Partial<TsrctDemandRequest>>;
    requestedBy: string;
  };
  loading?: boolean;
}

export const DemandRequestDetails = ({ data, loading }: DemandRequestDetailsProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRequestWidget' });
  const { format } = useCurrency({ code: data.request?.currency || DEFAULT_CURRENCY });
  const widgetConfig = useModuleConfig<DemandRequestWidgetConfig>();
  const items = data.request?.lineItems;

  const apiRef = useGridApiRef();
  const config = useDemandRequestGridConfig();
  const { columnVisibilityModel } = config;

  const skip = loading;

  const getDetailPanelContent = useCallback(<R extends GridValidRowModel = any>({ row }: GridRowParams<R>) => {
    return <RowDetailPanel description={row.description} />;
  }, []);
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const primarySourceReference = data.request?.sourceReferences?.find((s) => s.isPrimary);

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignContent: 'flex-start',
        }}
      >
        <Grid container size={{ xs: 12, md: 6 }} spacing={4}>
          <WidgetField fieldConfig={widgetConfig.externalReference}>
            <Grid
              container
              size={{ xs: 12, sm: 4 }}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  display: 'block',
                }}
              >
                {t('externalReference')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              {loading ? (
                <Skeleton />
              ) : primarySourceReference ? (
                <PurchaseRequisitionSourceReference
                  reference={{
                    purchaseRequisitionId: primarySourceReference.internalId,
                    links: [
                      {
                        name: primarySourceReference.sourceSystem.name,
                        url: primarySourceReference.url,
                      },
                    ],
                    referenceId: primarySourceReference.sourceReference,
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="textPrimary"
                  sx={{
                    display: 'block',
                  }}
                >
                  {data?.request?.externalReference || NULLISH_VALUE}
                </Typography>
              )}
            </Grid>
          </WidgetField>
          <WidgetField fieldConfig={widgetConfig.commodityGroup}>
            <Grid
              container
              size={{ xs: 12, sm: 4 }}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  display: 'block',
                }}
              >
                {t('commodityGroup')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Typography
                variant="body2"
                color="textPrimary"
                sx={{
                  display: 'block',
                }}
              >
                {loading ? (
                  <Skeleton />
                ) : data.request?.commodityGroupId ? (
                  <CuriosityLinkChip
                    ns={CuriosityNamespaceType.COMMODITY_GROUP}
                    i18nKey={data.request?.commodityGroupId}
                  />
                ) : (
                  NULLISH_CELL_VALUE
                )}
              </Typography>
            </Grid>
          </WidgetField>
          <WidgetField fieldConfig={widgetConfig.budget}>
            <Grid
              container
              size={{ xs: 12, sm: 4 }}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  display: 'block',
                }}
              >
                {t('budget')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Typography
                variant="body2"
                color="textPrimary"
                sx={{
                  display: 'block',
                }}
              >
                {loading ? <Skeleton /> : data.request?.budget ? format(data.request?.budget) : NULLISH_CELL_VALUE}
              </Typography>
            </Grid>
          </WidgetField>
          <Grid
            container
            size={{ xs: 12, sm: 4 }}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                display: 'block',
              }}
            >
              {data?.requestedBy ? (
                <CuriosityValue
                  ns={CuriosityNamespaceType.INTERNAL_USER}
                  i18nKey={data.requestedBy}
                  fallback={t('description')}
                  valueGetter={(ct) => t('descriptionRequester', ct.entity)}
                ></CuriosityValue>
              ) : (
                t('description')
              )}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography
              variant="body2"
              color="textPrimary"
              sx={{
                display: 'block',
              }}
            >
              {loading ? (
                <Skeleton />
              ) : data.request?.summary ? (
                <Multiline>{data.request?.summary}</Multiline>
              ) : (
                NULLISH_CELL_VALUE
              )}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {!skip && items ? (
        <>
          <DataGridWidgetHeader disablePadding left={<Typography variant="body2">{t('positions')}</Typography>} />
          <DataGridProStyled
            {...config}
            apiRef={apiRef}
            autoHeight
            hideFooter
            disableColumnMenu
            loading={loading}
            rows={items}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            slots={{
              detailPanelExpandIcon: ChevronRightOutlined,
              detailPanelCollapseIcon: ExpandMoreOutlined,
            }}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'default' },
            }}
            rowSelection={false}
            columnVisibilityModel={columnVisibilityModel}
          />
        </>
      ) : null}
    </>
  );
};
